import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  updateDoc, 
  doc,
  serverTimestamp,
  limit 
} from "firebase/firestore";
import { db } from "../firebase";
import { buildWorkflowSnapshot, getSplitWorkflowSections, isWorkflowDraftAccessibleToUser } from "./workflowUtils";

/**
 * Get or create a draft submission for a form
 * @param {string} companyId 
 * @param {object} form - Form object with id and title
 * @param {string} userId 
 * @param {string} assignedTo - Optional assignment ID for scheduled checks
 * @returns {object} - Draft submission with id
 */
export async function getOrCreateDraft(companyId, form, userId, assignedTo = null, userProfile = null) {
  const submissionsRef = collection(db, "companies", companyId, "submissions");
  const workflowSections = buildWorkflowSnapshot(form?.fields || []);
  const splitWorkflowSections = getSplitWorkflowSections(workflowSections);
  const hasSplitWorkflow = splitWorkflowSections.length > 0;
  
  // Check for existing draft
  const q = hasSplitWorkflow
    ? query(
        submissionsRef,
        where("formId", "==", form.id),
        where("status", "==", "draft"),
        limit(20)
      )
    : query(
        submissionsRef,
        where("formId", "==", form.id),
        where("createdBy", "==", userId),
        where("status", "==", "draft"),
        limit(1)
      );

  const snap = await getDocs(q);

  const existingDoc = hasSplitWorkflow
    ? snap.docs.find((draftDoc) => isWorkflowDraftAccessibleToUser({ id: draftDoc.id, ...draftDoc.data() }, userProfile))
    : snap.docs[0];

  if (existingDoc) {
    const existing = existingDoc;
    return { id: existing.id, ...existing.data() };
  }

  // Create new draft
  const ref = await addDoc(submissionsRef, {
    formId: form.id,
    formTitle: form.title || "Untitled Form",
    createdBy: userId,
    conductedBy: userId,
    assignedTo: assignedTo,
    status: "draft",
    workflowSections,
    answers: {},
    sectionProgress: {
      currentSectionIndex: 0,
      completedSections: []
    },
    startedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdAt: serverTimestamp()
  });

  return { 
    id: ref.id, 
    formId: form.id,
    formTitle: form.title,
    createdBy: userId,
    conductedBy: userId,
    status: "draft",
    workflowSections,
    answers: {},
    sectionProgress: {
      currentSectionIndex: 0,
      completedSections: []
    }
  };
}

/**
 * Save draft progress (debounced on caller side)
 * @param {string} companyId 
 * @param {string} submissionId 
 * @param {object} data - Data to update
 */
export async function saveDraft(companyId, submissionId, data) {
  if (!companyId) {
    throw new Error("Company ID is required to save draft");
  }
  if (!submissionId) {
    throw new Error("Submission ID is required to save draft");
  }
  
  const submissionRef = doc(db, "companies", companyId, "submissions", submissionId);
  
  try {
    await updateDoc(submissionRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error("saveDraft error:", error);
    throw new Error(`Failed to save draft: ${error.message}`);
  }
}

/**
 * Submit a draft (change status to submitted)
 * @param {string} companyId 
 * @param {string} submissionId 
 * @param {object} finalData - Final submission data
 */
export async function submitDraft(companyId, submissionId, finalData = {}) {
  const submissionRef = doc(db, "companies", companyId, "submissions", submissionId);
  
  await updateDoc(submissionRef, {
    ...finalData,
    status: "submitted",
    submittedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
}

/**
 * Get all drafts for a user
 * @param {string} companyId 
 * @param {string} userId 
 * @returns {array} - Array of draft submissions
 */
export async function getUserDrafts(companyId, userProfileOrUserId) {
  const submissionsRef = collection(db, "companies", companyId, "submissions");
  const userProfile = typeof userProfileOrUserId === "string"
    ? { uid: userProfileOrUserId }
    : (userProfileOrUserId || {});
  const uid = userProfile?.uid || userProfile?.id || userProfile?.userId || null;
  
  const q = query(
    submissionsRef,
    where("status", "==", "draft")
  );

  const snap = await getDocs(q);
  return snap.docs
    .map((draftDoc) => ({ id: draftDoc.id, ...draftDoc.data() }))
    .filter((draft) => {
      if (uid && draft.createdBy === uid) {
        return true;
      }
      return isWorkflowDraftAccessibleToUser(draft, userProfile);
    });
}

/**
 * Delete a draft
 * @param {string} companyId 
 * @param {string} submissionId 
 */
export async function deleteDraft(companyId, submissionId) {
  const submissionRef = doc(db, "companies", companyId, "submissions", submissionId);
  await updateDoc(submissionRef, {
    status: "deleted",
    deletedAt: serverTimestamp()
  });
}

/**
 * Validate if submission is ready to submit
 * @param {object} form - Form definition
 * @param {object} answers - User answers
 * @returns {object} - { valid: boolean, missingFields: array }
 */
export function validateSubmission(form, answers) {
  const missingFields = [];
  
  if (!form.sections) {
    return { valid: true, missingFields: [] };
  }

  form.sections.forEach(section => {
    if (!section.fields) return;
    
    section.fields.forEach(field => {
      if (field.required) {
        const answer = answers[field.id];
        
        if (answer === undefined || answer === null || answer === "") {
          missingFields.push({
            fieldId: field.id,
            fieldLabel: field.label || field.id,
            sectionTitle: section.title
          });
        }
        
        // Check arrays (checkboxes, multi-select)
        if (Array.isArray(answer) && answer.length === 0) {
          missingFields.push({
            fieldId: field.id,
            fieldLabel: field.label || field.id,
            sectionTitle: section.title
          });
        }
      }
    });
  });

  return {
    valid: missingFields.length === 0,
    missingFields
  };
}
